import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/dxchange.png";
import DXchangeIPaaS from "../../assets/images/DXchange-iPaaS.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "DXchange, Enterprise Data Exchange Middleware & ESB Platform | Avani Cimcon",
  description:
    "DXchange is Avani Cimcon's enterprise data exchange middleware heritage platform. It demonstrates ESB architecture, XML web services, business rules and scalable integration engineering.",
  keywords:
    "Middleware Software Solutions, Data Exchange Middleware Server, Data Exchange Middleware Server software, d exchange, middleware server, Exchange server, Exchange Web services XM, SOA middleware, xml middleware, biztalk middleware, xml software, data exchange, data integration software, xml applications, XML web services, Business Rules Engine, Exchange server, Dxchange- xml middleware software, middleware software directory, Exchange Web Service, XML Web Services API: Integration, Middleware, xml Technology, Middleware Software, XML Web Services Middleware, Middleware Software, Middleware Solutions, Middleware Server, Middleware Systems",
  canonical: "https://www.avanicimcon.com/dxchange",
  ogImage: "https://www.avanicimcon.com/images/banner/dxchange.png",
  hero: {
    eyebrow: "Product Heritage",
    heading: "DXchange",
    lead:
      "DXchange shows how Avani Cimcon solved enterprise integration problems by designing a configurable XML web services backbone, not by selling a generic integration slogan.",
    image: BannerImg,
    tags: ["ESB architecture", "XML web services", "Middleware", "Enterprise integration"],
    panelKicker: "Platform thinking before platform marketing.",
    panelTitle: "A data exchange backbone built for complex enterprise interoperability.",
    panelText:
      "The platform connected existing applications and data through custom adapters so organizations could expose capabilities as web services and evolve without a full replacement.",
    stats: [
      { value: "DXchange", label: "Productized integration platform" },
      { value: "ESB", label: "Enterprise service bus architecture" },
      { value: "NASSCOM", label: "Recognized innovation proof" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
    externalLinks: [{ label: "Visit DXchange Website", href: "http://dxchange.travelcarma.com" }],
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "An integration platform built to expose enterprise systems as services",
    lead:
      "DXchange is a strong proof point for enterprise architecture because it shows how the company handled integration complexity, service exposure and configurable delivery long before AI became a market term.",
    paragraphs: [
      "DXchange is an XML web services based, fully scalable and easily configurable data exchange middleware server. It provides the infrastructure for an organization or an application to form a powerful XML web services communications backbone.",
      "It is built on the concept of an Enterprise Service Bus. Using custom adapters, existing applications and data can be integrated with the DXchange bus and exposed as web services so the platform can evolve incrementally.",
    ],
    callout: {
      title: "What the page should prove",
      text: "This is not a brochure page. It is evidence that Avani Cimcon has already engineered reusable integration infrastructure for enterprise environments.",
      items: [
        "Service exposure through web services",
        "Custom adapters for heterogeneous systems",
        "Incremental modernization rather than replacement",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Enterprises needed a way to connect disconnected applications and data",
    lead:
      "DXchange was built for the operational reality of fragmented systems, multiple protocols and changing integration requirements.",
    items: [
      {
        title: "Disconnected enterprise systems",
        text: "Organizations needed one communications backbone that could connect legacy applications, databases and new services.",
      },
      {
        title: "Heterogeneous integration",
        text: "The platform had to work across different applications, technologies and vertical use cases without forcing a rewrite.",
      },
      {
        title: "Operational change over time",
        text: "Business requirements changed constantly, so the architecture needed rules and adapter flexibility instead of hard-coded flows.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "ESB-style design with adapter-based integration and service exposure",
    lead:
      "The architecture story matters because it explains why DXchange remains useful as proof of platform engineering capability.",
    paragraphs: [
      "DXchange is platform agnostic because all interfaces are through web services, including admin and maintenance interfaces. That made the product suitable for enterprise operations where maintainability matters as much as raw connectivity.",
      "The integrated Business Rules Engine gave the flexibility to keep pace with changing requirements without programming or code changes, which is a strong example of design thinking around operational complexity.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "XML web services backbone",
        "Custom adapters for system integration",
        "Rules-driven flexibility for changing business needs",
        "Web UI for enterprise administration",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The platform was productized into clear delivery tiers",
    lead:
      "Those tiers are proof of product thinking because they show architecture that could be scaled and sold across different enterprise needs.",
    items: [
      {
        title: "DXchange Lite",
        text: "Supports individual applications that either consume data from external sources or provide data as a service.",
      },
      {
        title: "DXchange Professional",
        text: "Supports two-way consumption and exposure of services, functioning as a true middleware gateway with pass-through connections.",
      },
      {
        title: "DXchange Enterprise",
        text: "Provides an enterprise service bus with broad application connectivity and a web-based business rules engine.",
      },
      {
        title: "Platform Agnostic Interfaces",
        text: "Admin and maintenance interfaces were exposed through web services, supporting long-term operational flexibility.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "Platform complexity was handled through modularity, rules and adapters",
    lead:
      "The engineering challenge was not just transport. It was creating a flexible system that could adapt to many enterprise contexts.",
    items: [
      {
        title: "Multi-system orchestration",
        text: "One bus had to serve different applications, domains and integration endpoints without losing control.",
      },
      {
        title: "Business rule variability",
        text: "Rules needed to change without full code rewrites, reducing change cost and delivery risk.",
      },
      {
        title: "Scale across verticals",
        text: "The same foundation had to be adaptable for travel, insurance, legal and other enterprise scenarios.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The platform was designed to scale in capability as the enterprise grew",
    lead:
      "The product versions themselves are evidence of planned scaling, not ad hoc feature growth.",
    paragraphs: [
      "Each version could be scaled to the next one easily, allowing organizations to start with a smaller deployment and move toward a broader enterprise bus as requirements grew.",
      "That made DXchange a useful proof page for Avani Cimcon because it demonstrates architectural discipline, operations awareness and a willingness to engineer for growth from the start.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Scale without abandoning the original platform",
        "Expose services instead of creating silos",
        "Support enterprise maintenance and administration",
      ],
    },
  },
  integration: {
    eyebrow: "Technology & Integration Experience",
    title: "The platform connected existing applications, data and services through custom adapters",
    lead:
      "This is where DXchange links directly to modernization work, because it is evidence of integration-first thinking.",
    paragraphs: [
      "Utilizing custom adapters, existing applications and data could be integrated with the DXchange bus and accessed as web services. That pattern is the same practical mindset modern enterprises need when they want to modernize incrementally.",
      "DXchange also preserved relevance by remaining flexible for different application and industry vertical combinations, which is exactly the sort of experience that matters to clients facing integration debt today.",
    ],
    callout: {
      title: "Integration capabilities",
      items: [
        "Web services exposure",
        "Custom adapter model",
        "Cross-application interoperability",
        "Rules-aware integration behavior",
      ],
    },
  },
  media: {
    eyebrow: "Platform Imagery",
    title: "The DXchange platform graphic remains part of the proof story",
    lead:
      "Keeping the platform visual visible helps preserve the engineering narrative associated with the page.",
    items: [
      {
        src: DXchangeIPaaS,
        alt: "DXchange enterprise integration platform diagram",
        caption: "DXchange platform overview",
        text: "The diagram reinforces the ESB and middleware architecture story behind the page.",
      },
    ],
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "The DXchange story proves Avani Cimcon can modernize complex environments safely",
    lead:
      "The value to clients is not the product itself. The value is the engineering method behind it.",
    items: [
      {
        title: "Integration-first modernization",
        text: "We understand how to work with existing systems and expose capability without forcing a replacement program.",
      },
      {
        title: "Platform thinking",
        text: "We know how to design reusable foundations that can support multiple business contexts.",
      },
      {
        title: "Operational realism",
        text: "We understand that admin, maintenance and change management are part of the product, not afterthoughts.",
      },
      {
        title: "Legacy preservation",
        text: "We can keep valuable systems connected while reducing technical fragmentation.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "The platform's architecture maps naturally to modern integration programs",
    lead:
      "DXchange is a proof point for reuse, incremental replacement and service-based modernization.",
    paragraphs: [
      "The original platform made it possible to expose legacy capabilities as web services, which is exactly the kind of pattern enterprises still need when modernizing without disruption.",
      "That same mindset supports incremental modernization programs where the integration layer is improved first and the operational core is left intact until the business is ready.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Preserve valuable legacy investments",
        "Improve integration without business interruption",
        "Use adapters and rules to reduce rewrite pressure",
        "Stage modernization around service exposure",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI can amplify integration platforms when the architecture is already solid",
    lead:
      "AI belongs here only after the engineering story is established, because the real value is in the platform foundation.",
    items: [
      {
        title: "Intelligent routing",
        text: "Use AI to help route integration flows, exceptions and data transformations more efficiently.",
      },
      {
        title: "Operational monitoring",
        text: "Use AI-assisted observability to detect failures, anomalies and service bottlenecks faster.",
      },
      {
        title: "Policy-aware automation",
        text: "Use AI to support rules-based orchestration while preserving the underlying business logic.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "DXchange connects directly to the industries where integration matters most",
    lead:
      "These links preserve the original domain relevance while strengthening the enterprise story.",
    items: [
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Reservation, content and supplier integration." },
      { label: "Insurance", href: "/insurance", text: "Policy, claims and legacy core system connectivity." },
      { label: "Legal", href: "/legal", text: "Case systems, documents and vendor exchange." },
      { label: "Healthcare", href: "/healthcare", text: "Clinical workflows and interoperability." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The platform reinforces the company’s broader engineering depth",
    lead:
      "These are the capabilities that make DXchange credible as a proof page instead of a brochure page.",
    items: [
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization and integration strategy." },
      { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven process orchestration." },
      { label: "CMS", href: "/cms", text: "Portal and content platform heritage." },
      { label: "Quality Assurance", href: "/quality-assurance", text: "Delivery reliability and testing discipline." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "DXchange proves Avani Cimcon has platform-building and enterprise architecture experience",
    lead:
      "The product is evidence of ownership, longevity and technical depth.",
    items: [
      {
        title: "Decades of engineering",
        text: "The platform reflects long-term delivery experience rather than one-off project work.",
      },
      {
        title: "Recognized innovation",
        text: "NASSCOM recognition adds external proof that the work had real market value.",
      },
      {
        title: "Integration mindset",
        text: "The architecture was designed to connect systems, not isolate them.",
      },
      {
        title: "Enterprise credibility",
        text: "DXchange is a direct example of Avani Cimcon building something complex and durable.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Use the DXchange mindset to modernize your own integration landscape",
    text:
      "If your enterprise needs a safer integration strategy, we can apply the same platform thinking to your modernization program.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class DXchange extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default DXchange;
